"use client";
import React, { useEffect, useRef } from "react";

const STEPS = [
  {
    number: "01",
    title: "Share your requirements.",
    description: "Tell us your budget, preferred location, and investment goals. First-time investor or seasoned buyer — we tailor our approach to you. One call is all it takes to get started.",
    detail: "Free consultation · No obligation",
  },
  {
    number: "02",
    title: "We shortlist verified plots.",
    description: "We present only legally verified, title-clear properties that match your criteria. No ambiguity. No hidden complications. Every plot comes with a full documentation review.",
    detail: "Legal verification included · All documents checked",
  },
  {
    number: "03",
    title: "Site visit and due diligence.",
    description: "We arrange a guided site visit — in person or virtual for NRIs. You see the land, understand the surroundings, and ask every question before committing to anything.",
    detail: "In-person or virtual · NRI-friendly",
  },
  {
    number: "04",
    title: "Deal closure and registration.",
    description: "We handle the paperwork, coordinate with legal teams, and guide you through registration. From agreement to possession — we stay with you until the deal is done.",
    detail: "End-to-end support · Transparent process",
  },
];

export default function HowItWorks() {
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add("visible");
        });
      },
      { threshold: 0.15 }
    );
    itemRefs.current.forEach((item) => { if (item) observer.observe(item); });
    return () => observer.disconnect();
  }, []);

  return (
    <section id="process" className="bg-linen py-24 px-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="max-w-xl mb-20">
          <span className="text-xs font-medium tracking-[0.3em] uppercase text-pine-light mb-4 block">
            The Process
          </span>
          <h2 className="font-serif text-display-sm text-pine font-light leading-tight">
            Four steps.<br />
            <em className="italic">One clear path.</em>
          </h2>
        </div>

        {/* Steps Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {STEPS.map((step, index) => (
            <div
              key={step.number}
              ref={(el) => { itemRefs.current[index] = el; }}
              className="reveal relative"
              style={{ transitionDelay: `${index * 0.1}s` }}>

              {/* Connector line */}
              {index < STEPS.length - 1 && (
                <div className="hidden lg:block absolute top-8 left-full w-full h-px bg-fog/60 z-0" style={{ width: "calc(100% - 2rem)" }} />
              )}

              <div className="relative z-10">
                <div className="w-16 h-16 rounded-2xl bg-amber/10 flex items-center justify-center mb-6">
                  <span className="font-serif text-2xl font-light text-amber">{step.number}</span>
                </div>
                <h3 className="font-serif text-xl font-light text-pine leading-tight mb-3">
                  {step.title}
                </h3>
                <p className="text-sm text-pine-light font-light leading-relaxed mb-4">
                  {step.description}
                </p>
                <div className="flex items-center gap-2 text-xs font-medium text-pine-light">
                  <div className="w-6 h-px bg-amber" />
                  {step.detail}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* About Section */}
        <div className="mt-24 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <span className="text-xs font-medium tracking-[0.3em] uppercase text-pine-light mb-4 block">
              About Us
            </span>
            <h2 className="font-serif text-display-sm text-pine font-light leading-tight mb-6">
              We are not brokers.<br />
              <em className="italic">We are your investment partner.</em>
            </h2>
            <p className="text-base text-pine-light font-light leading-relaxed mb-5">
              Malik Luxury Estates was built on one belief: land in Kashmir is one of the most undervalued assets in India. We have spent over a decade understanding this market — its geography, its legal landscape, and its growth potential.
            </p>
            <p className="text-base text-pine-light font-light leading-relaxed mb-8">
              We don&apos;t push listings. We advise. We help first-time investors, working professionals, and NRIs make decisions they will be proud of in 10 years. Every recommendation we make is one we would make for our own family.
            </p>
            <div className="flex items-center gap-2 text-sm font-medium text-pine">
              <div className="w-8 h-px bg-amber" />
              Founded in Kashmir. Built on trust.
            </div>
          </div>

          {/* Services */}
          <div id="services" className="space-y-4">
            <span className="text-xs font-medium tracking-[0.3em] uppercase text-pine-light mb-6 block">
              Our Services
            </span>
            {[
              {
                title: "Property Sourcing",
                detail: "We identify and shortlist plots that match your investment goals. Only verified, title-clear land makes it to your list.",
              },
              {
                title: "Investment Consulting",
                detail: "Not sure where to invest or how much? We analyse your goals and recommend the right zones, budgets, and timelines.",
              },
              {
                title: "Buy / Sell Assistance",
                detail: "Whether you are buying your first plot or selling an existing asset, we manage the entire transaction from start to finish.",
              },
              {
                title: "Legal Verification",
                detail: "Every property we recommend goes through a thorough legal check. Title clarity, encumbrance certificates, and registration support — all included.",
              },
            ].map((service) => (
              <div key={service.title} className="bg-fog/30 rounded-2xl p-5 border border-fog/60">
                <h4 className="font-medium text-pine text-sm mb-1.5">{service.title}</h4>
                <p className="text-xs text-pine-light font-light leading-relaxed">{service.detail}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}